/**
 * ICU/cEEG monitoring workbench — dedicated hospital-OS surface (Epic B1).
 * Composes cadence SLAs, escalation confirmation, compliance dashboard,
 * trend→raw panel config, and patient seizure-template screening assist.
 *
 * Escalations and seizure templates are persisted via clinician-gap APIs
 * (eeg_icu_escalation_event / eeg_patient_seizure_template).
 */

import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Table,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import apiClient from "@shared/services/apiClient";
import { useAuth } from "@shared/context/AuthContext";
import {
  createIcuWorkbenchSurface,
  screenIcuCandidateWithSeizureTemplates,
  type IcuWorkbenchStudyRow,
} from "../utils/icuCeegWorkbench";
import {
  createIcuEscalationEvent,
  type IcuEscalationEvent,
} from "../utils/consensusAndIcuEscalation";
import {
  createPatientSeizureTemplate,
  type PatientSeizureTemplate,
} from "../utils/detectorPerfAndSeizureTemplates";
import {
  confirmIcuEscalationApi,
  createIcuEscalationApi,
  listIcuEscalationsApi,
  listPatientSeizureTemplatesApi,
  upsertPatientSeizureTemplateApi,
} from "../services/clinicianGapPersistenceClient";
import { fetchQeegLongStudyTrends } from "../utils/qeegLongStudyTrendClient";
import type { QeegLongStudyClientSeries } from "../utils/qeegLongStudyTrendClient";
import type { QEEGTrendProvenance, QEEGTrendType } from "../utils/qeegTrendTransforms";
import { QEEG_PANEL_CATALOG } from "../utils/qeegTrendPanelConfig";

const QEEGTrendLane = lazy(() => import("./QEEGTrendLane"));

type WorklistItem = {
  eeg_study_id?: string;
  recording_id?: string | null;
  patient_id?: string | null;
  patient_name?: string | null;
  patient_mrn?: string | null;
  study_date?: string | null;
  last_reviewed_at?: string | null;
  oldest_pending_candidate_at?: string | null;
  last_report_at?: string | null;
  first_review_completed_at?: string | null;
  significant_change?: boolean;
  status?: string | null;
  priority?: string | null;
  study_type?: string | null;
  modality?: string | null;
  monitoring_type?: string | null;
  service_line?: string | null;
  location?: string | null;
  unit?: string | null;
};

const ESCALATION_LABELS: Record<IcuEscalationEvent["kind"], string> = {
  notify_expert: "Notify expert reviewer",
  bedside_mode: "Bedside review requested",
  qeeg_alert_confirmation: "qEEG alert confirmation",
  therapy_change_checkpoint: "Therapy change checkpoint",
};

const ESCALATION_DESCRIPTIONS: Record<IcuEscalationEvent["kind"], string> = {
  notify_expert:
    "Requests specialist review for a concerning ICU/cEEG finding or workflow question.",
  bedside_mode:
    "Flags that bedside clinical review or bedside team coordination is needed.",
  qeeg_alert_confirmation:
    "Blocks report-impacting qEEG actions until the alert is confirmed by a reviewer.",
  therapy_change_checkpoint:
    "Requires review before documenting therapy-change impact.",
};

const TREND_PANEL_LABELS: Record<string, string> = {
  amplitude_aeeg: "Amplitude-integrated EEG",
  cdsa_spectrogram: "CDSA spectrogram",
  rhythmicity: "Rhythmicity",
  asymmetry: "Hemispheric asymmetry",
  alpha_delta_variability: "Alpha-delta variability",
};

function isIcuCeegWorklistItem(item: WorklistItem): boolean {
  const searchable = [
    item.study_type,
    item.modality,
    item.monitoring_type,
    item.service_line,
    item.location,
    item.unit,
    item.priority,
  ]
    .filter(Boolean)
    .join(" ")
    .replace(/[_-]/g, " ")
    .toLowerCase();
  return /\b(icu|ceeg|c-eeg|continuous|critical|neonatal|ltm)\b/.test(searchable);
}

function mapWorklistItemToIcuRow(item: WorklistItem): IcuWorkbenchStudyRow | null {
  if (!item.eeg_study_id) return null;
  const patientLabel =
    [item.patient_mrn, item.patient_name].filter(Boolean).join(" / ") ||
    item.eeg_study_id;
  return {
    studyId: item.eeg_study_id,
    patientLabel,
    recordingId: item.recording_id ?? null,
    monitoringStartedAtIso: item.study_date || new Date().toISOString(),
    firstReviewCompletedAtIso: item.first_review_completed_at ?? null,
    lastReportAtIso: item.last_report_at ?? null,
    lastReviewedAtIso: item.last_reviewed_at ?? null,
    oldestPendingCandidateIso: item.oldest_pending_candidate_at ?? null,
    significantChange: Boolean(item.significant_change),
    pendingEscalations: [],
  };
}

function mapTemplateRow(row: any): PatientSeizureTemplate | null {
  const payload = (row?.payload_json || row || {}) as Partial<PatientSeizureTemplate>;
  const templateId = String(row?.template_id || payload.templateId || "");
  const patientId = String(row?.patient_id || payload.patientId || "");
  if (!templateId || !patientId) return null;
  return {
    templateId,
    patientId,
    admissionId: String(row?.admission_id || payload.admissionId || ""),
    label: String(payload.label || "Admission seizure template"),
    channelSet: Array.isArray(payload.channelSet) ? payload.channelSet : [],
    startTimeSeconds: Number(payload.startTimeSeconds || 0),
    endTimeSeconds: Number(payload.endTimeSeconds || 0),
    morphologyTags: Array.isArray(payload.morphologyTags)
      ? payload.morphologyTags
      : [],
    confirmedBy: String(payload.confirmedBy || row?.created_by || ""),
    confirmedAtIso: String(
      payload.confirmedAtIso || row?.updated_at || new Date().toISOString(),
    ),
    schemaVersion: payload.schemaVersion || "seizure-template-v1.0.0",
  };
}

export default function IcuCeegWorkbenchPanel() {
  const { user } = useAuth();
  const actorId = user?.id || "";
  const [rows, setRows] = useState<IcuWorkbenchStudyRow[]>([]);
  const [patientIdByStudy, setPatientIdByStudy] = useState<Record<string, string>>(
    {},
  );
  const [escalations, setEscalations] = useState<IcuEscalationEvent[]>([]);
  const [templates, setTemplates] = useState<PatientSeizureTemplate[]>([]);
  const [selectedStudyId, setSelectedStudyId] = useState("");
  const [screeningNote, setScreeningNote] = useState<string | null>(null);
  const [loadingRows, setLoadingRows] = useState(true);
  const [persisting, setPersisting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [liveTrendSeries, setLiveTrendSeries] = useState<
    QeegLongStudyClientSeries[] | null
  >(null);
  const [liveTrendStatus, setLiveTrendStatus] = useState<
    "idle" | "loading" | "ready" | "partial" | "unavailable" | "generating" | "failed"
  >("idle");
  const [liveTrendProvenance, setLiveTrendProvenance] =
    useState<QEEGTrendProvenance | null>(null);
  const [cadenceConfig, setCadenceConfig] = useState<{
    firstReviewMinutesMax: number;
    reportCadenceHours: number;
  } | null>(null);

  const loadRows = useCallback(async () => {
    setLoadingRows(true);
    try {
      const [response, slaRes] = await Promise.all([
        apiClient.get("/dashboard/worklist"),
        apiClient.get("/dashboard/clinical-sla").catch(() => null),
      ]);
      const policy = slaRes?.data?.data || slaRes?.data;
      if (policy?.ceeg) {
        setCadenceConfig({
          firstReviewMinutesMax: Number(policy.ceeg.firstReviewMinutes) || 60,
          reportCadenceHours: Number(policy.ceeg.reportCadenceHours) || 24,
        });
      }
      const data: WorklistItem[] = response.data?.data ?? [];
      const nextPatientIds: Record<string, string> = {};
      const nextRows = data
        .filter(isIcuCeegWorklistItem)
        .map((item) => {
          if (item.eeg_study_id && item.patient_id) {
            nextPatientIds[item.eeg_study_id] = item.patient_id;
          }
          return mapWorklistItemToIcuRow(item);
        })
        .filter((row): row is IcuWorkbenchStudyRow => Boolean(row));
      setRows(nextRows);
      setPatientIdByStudy(nextPatientIds);
      setSelectedStudyId((current) =>
        current && nextRows.some((row) => row.studyId === current)
          ? current
          : nextRows[0]?.studyId || "",
      );
      setLoadError(null);
    } catch (error: any) {
      setRows([]);
      setSelectedStudyId("");
      setLoadError(
        error?.response?.data?.error ||
          "Unable to load ICU/cEEG studies from the clinical worklist.",
      );
    } finally {
      setLoadingRows(false);
    }
  }, []);

  const reloadEscalationsForStudies = useCallback(async (studyIds: string[]) => {
    if (studyIds.length === 0) {
      setEscalations([]);
      return;
    }
    const results = await Promise.all(
      studyIds.map(async (studyId) => {
        try {
          return await listIcuEscalationsApi(studyId);
        } catch {
          return [] as IcuEscalationEvent[];
        }
      }),
    );
    setEscalations(results.flat());
  }, []);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  useEffect(() => {
    if (rows.length === 0) {
      setEscalations([]);
      return;
    }
    void reloadEscalationsForStudies(rows.map((row) => row.studyId));
  }, [rows, reloadEscalationsForStudies]);

  useEffect(() => {
    if (!selectedStudyId) {
      setTemplates([]);
      return;
    }
    const patientId = patientIdByStudy[selectedStudyId];
    if (!patientId) {
      setTemplates([]);
      return;
    }
    void listPatientSeizureTemplatesApi({
      patientId,
      admissionId: selectedStudyId,
    })
      .then((rows) => {
        setTemplates(
          rows
            .map(mapTemplateRow)
            .filter((row): row is PatientSeizureTemplate => Boolean(row)),
        );
      })
      .catch(() => setTemplates([]));
  }, [patientIdByStudy, selectedStudyId]);

  const rowsWithEscalations = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        pendingEscalations: escalations.filter((e) => e.studyId === row.studyId),
      })),
    [rows, escalations],
  );

  const surface = useMemo(
    () =>
      createIcuWorkbenchSurface(
        rowsWithEscalations,
        new Date(),
        cadenceConfig || undefined,
      ),
    [rowsWithEscalations, cadenceConfig],
  );
  const dashboard = surface.dashboard;
  const trendPanelLabels = surface.trendPanelConfig.enabledPanelIds.map(
    (panelId) => TREND_PANEL_LABELS[panelId] || panelId,
  );

  const selectedStudy = rows.find((row) => row.studyId === selectedStudyId) || null;
  const selectedEscalations = escalations.filter(
    (event) => event.studyId === selectedStudyId,
  );

  useEffect(() => {
    const recordingId = selectedStudy?.recordingId;
    if (!recordingId) {
      setLiveTrendSeries(null);
      setLiveTrendStatus("idle");
      setLiveTrendProvenance(null);
      return;
    }
    let cancelled = false;
    setLiveTrendStatus("loading");
    void (async () => {
      const result = await fetchQeegLongStudyTrends({ recordingId });
      if (cancelled) return;
      if (result.ok && result.data) {
        setLiveTrendSeries(result.data.series);
        const p = result.data.provenance;
        setLiveTrendProvenance(
          p
            ? {
                algorithmVersion: p.algorithmVersion,
                channelSet: p.channelSet || [],
                montage: p.montage ?? null,
                samplingRateHz: p.samplingRateHz,
                filters: {
                  highPassHz: p.filters?.highPassHz ?? null,
                  lowPassHz: p.filters?.lowPassHz ?? null,
                  notch50Hz: Boolean(p.filters?.notch50Hz),
                  notch60Hz: Boolean(p.filters?.notch60Hz),
                },
                artifactExclusions: p.artifactExclusions || [],
                epochSeconds: p.epochSeconds,
                generatedAtIso: p.generatedAtIso,
              }
            : null,
        );
        setLiveTrendStatus(
          result.data.progress.status === "partial" ? "partial" : "ready",
        );
        return;
      }
      setLiveTrendSeries(null);
      setLiveTrendProvenance(result.data?.provenance ?? null);
      const status = result.data?.progress?.status;
      if (status === "generating") {
        setLiveTrendStatus("generating");
      } else if (status === "failed") {
        setLiveTrendStatus("failed");
      } else {
        setLiveTrendStatus("unavailable");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedStudy?.recordingId]);

  const enabledTrendTypes = useMemo(() => {
    const types = new Set<string>();
    for (const panelId of surface.trendPanelConfig.enabledPanelIds) {
      const def = QEEG_PANEL_CATALOG.find((p) => p.id === panelId);
      def?.trendTypes.forEach((t) => types.add(t));
    }
    // Always surface CDSA / spectrogram when catalog enables cdsa_spectrogram.
    if (surface.trendPanelConfig.enabledPanelIds.includes("cdsa_spectrogram")) {
      types.add("spectrogram");
      types.add("dsa");
    }
    return Array.from(types) as QEEGTrendType[];
  }, [surface.trendPanelConfig.enabledPanelIds]);

  const raiseEscalation = async (kind: IcuEscalationEvent["kind"]) => {
    if (!selectedStudyId || persisting) return;
    const existing = escalations.find(
      (event) =>
        event.studyId === selectedStudyId &&
        event.kind === kind &&
        !event.confirmed,
    );
    if (existing) {
      setActionError(
        "An open escalation of this type already exists. Confirm it instead of recreating.",
      );
      return;
    }

    setPersisting(true);
    setActionError(null);
    try {
      const event = createIcuEscalationEvent({
        eventId: crypto.randomUUID(),
        studyId: selectedStudyId,
        kind,
        message: ESCALATION_DESCRIPTIONS[kind],
      });
      await createIcuEscalationApi(selectedStudyId, event);
      await reloadEscalationsForStudies(rows.map((row) => row.studyId));
    } catch (error: any) {
      setActionError(
        error?.response?.data?.error ||
          error?.message ||
          "Failed to persist ICU escalation.",
      );
    } finally {
      setPersisting(false);
    }
  };

  const confirmSelected = async () => {
    if (!actorId || persisting) return;
    const pending = selectedEscalations.filter(
      (event) => event.requiresConfirmation && !event.confirmed,
    );
    if (pending.length === 0) return;

    setPersisting(true);
    setActionError(null);
    try {
      await Promise.all(
        pending.map((event) => confirmIcuEscalationApi(event.eventId)),
      );
      await reloadEscalationsForStudies(rows.map((row) => row.studyId));
    } catch (error: any) {
      setActionError(
        error?.response?.data?.error ||
          error?.message ||
          "Failed to confirm ICU escalation.",
      );
    } finally {
      setPersisting(false);
    }
  };

  const addTemplateAndScreen = async () => {
    if (!selectedStudyId || !actorId || persisting) return;
    const patientId = patientIdByStudy[selectedStudyId];
    if (!patientId) {
      setActionError(
        "Selected study is missing a patient_id from the worklist; cannot save a seizure template.",
      );
      return;
    }

    setPersisting(true);
    setActionError(null);
    try {
      const template = createPatientSeizureTemplate({
        templateId: crypto.randomUUID(),
        patientId,
        admissionId: selectedStudyId,
        label: "Admission seizure template",
        channelSet: ["Fp1", "F3", "C3"],
        startTimeSeconds: 10,
        endTimeSeconds: 40,
        confirmedBy: actorId,
      });
      await upsertPatientSeizureTemplateApi({
        patientId,
        admissionId: selectedStudyId,
        studyId: selectedStudyId,
        payload: template,
      });
      const nextRows = await listPatientSeizureTemplatesApi({
        patientId,
        admissionId: selectedStudyId,
      });
      const nextTemplates = nextRows
        .map(mapTemplateRow)
        .filter((row): row is PatientSeizureTemplate => Boolean(row));
      setTemplates(nextTemplates);
      const screening = screenIcuCandidateWithSeizureTemplates({
        channelSet: ["Fp1", "F3", "C3"],
        durationSeconds: 28,
        templates: nextTemplates,
        baseThreshold: 0.7,
        admissionId: selectedStudyId,
      });
      setScreeningNote(
        screening.screeningAssistActive
          ? `Template match ${screening.bestTemplateId}; tuned threshold ${screening.tunedThreshold.toFixed(2)}`
          : "No template match for candidate",
      );
    } catch (error: any) {
      setActionError(
        error?.response?.data?.error ||
          error?.message ||
          "Failed to save seizure template.",
      );
    } finally {
      setPersisting(false);
    }
  };

  const markFirstReview = () => {
    if (!selectedStudyId) return;
    setRows((prev) =>
      prev.map((row) =>
        row.studyId === selectedStudyId
          ? {
              ...row,
              firstReviewCompletedAtIso: row.firstReviewCompletedAtIso
                ? null
                : new Date().toISOString(),
              lastReviewedAtIso: row.firstReviewCompletedAtIso
                ? row.lastReviewedAtIso
                : new Date().toISOString(),
            }
          : row,
      ),
    );
  };

  return (
    <Container fluid className="py-3" data-testid="icu-ceeg-workbench">
      <Row className="mb-3">
        <Col>
          <h2 className="h4 mb-1">ICU / cEEG monitoring workbench</h2>
          <div className="text-muted small">
            Tracks first-review due within 60 minutes, 24-hour report cadence,
            bedside escalation, synchronized trends-to-raw EEG review, and
            seizure-pattern screening for admitted ICU patients.
          </div>
        </Col>
        <Col xs="auto">
          <Link className="btn btn-outline-secondary btn-sm" to="/monitoring/icu">
            Legacy ICU analytics
          </Link>
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={3}>
          <Card>
            <Card.Body>
              <div className="text-muted small">Studies</div>
              <div className="fs-4">{dashboard.totalStudies}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card border={dashboard.first60OverdueCount ? "danger" : undefined}>
            <Card.Body>
              <div className="text-muted small">First-60 overdue</div>
              <div className="fs-4">{dashboard.first60OverdueCount}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card border={dashboard.report24hOverdueCount ? "warning" : undefined}>
            <Card.Body>
              <div className="text-muted small">24h report overdue</div>
              <div className="fs-4">{dashboard.report24hOverdueCount}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body>
              <div className="text-muted small">Report actions blocked</div>
              <div className="fs-4">{dashboard.reportActionsBlockedCount}</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {loadError && (
        <Alert variant="warning" className="mb-3">
          {loadError}
        </Alert>
      )}
      {actionError && (
        <Alert
          variant="danger"
          className="mb-3"
          dismissible
          onClose={() => setActionError(null)}
        >
          {actionError}
        </Alert>
      )}

      {!loadingRows && !loadError && rows.length === 0 && (
        <Alert variant="info" className="mb-3">
          No ICU/cEEG studies were returned from the clinical worklist. This page
          no longer falls back to seed or demo patients.
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Header>Census compliance</Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0" data-testid="icu-compliance-table">
            <thead>
              <tr>
                <th>Patient</th>
                <th>First-60</th>
                <th>24h report</th>
                <th>Escalation</th>
                <th>Report actions</th>
                <th>Open</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.studies.map((study) => (
                <tr
                  key={study.studyId}
                  className={
                    study.studyId === selectedStudyId ? "table-active" : undefined
                  }
                  onClick={() => setSelectedStudyId(study.studyId)}
                  style={{ cursor: "pointer" }}
                >
                  <td>{study.patientLabel}</td>
                  <td>
                    {study.first60Overdue ? (
                      <Badge bg="danger">Overdue</Badge>
                    ) : (
                      <Badge bg="success">OK</Badge>
                    )}
                  </td>
                  <td>
                    {study.report24hOverdue ? (
                      <Badge bg="warning" text="dark">
                        Overdue
                      </Badge>
                    ) : (
                      <Badge bg="success">OK</Badge>
                    )}
                  </td>
                  <td>
                    {study.escalationOverdue ? (
                      <Badge bg="danger">Overdue</Badge>
                    ) : (
                      <Badge bg="secondary">On track</Badge>
                    )}
                  </td>
                  <td>
                    {study.reportActionsAllowed ? (
                      <Badge bg="success">Allowed</Badge>
                    ) : (
                      <Badge bg="danger">Blocked</Badge>
                    )}
                  </td>
                  <td>{study.openEscalationCount}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Row className="g-3">
        <Col lg={6}>
          <Card>
            <Card.Header>Bedside escalation / confirmation</Card.Header>
            <Card.Body>
              <Alert variant="light" className="border small">
                Escalations are saved to the database for the selected worklist
                study and shared with report workflows. Open escalations require
                confirmation rather than deletion so the audit trail stays intact.
                qEEG confirmation blocks report-impacting actions until confirmed.
              </Alert>
              <Form.Group className="mb-2">
                <Form.Label>Selected study</Form.Label>
                <Form.Select
                  value={selectedStudyId}
                  onChange={(e) => setSelectedStudyId(e.target.value)}
                  aria-label="Selected ICU study"
                  disabled={rows.length === 0}
                >
                  {rows.map((row) => (
                    <option key={row.studyId} value={row.studyId}>
                      {row.patientLabel}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <div className="d-flex flex-wrap gap-2 mb-3">
                <Button
                  size="sm"
                  variant={
                    selectedEscalations.some(
                      (e) => e.kind === "notify_expert" && !e.confirmed,
                    )
                      ? "primary"
                      : "outline-primary"
                  }
                  onClick={() => void raiseEscalation("notify_expert")}
                  disabled={!selectedStudyId || persisting}
                >
                  Notify expert
                </Button>
                <Button
                  size="sm"
                  variant={
                    selectedEscalations.some(
                      (e) => e.kind === "bedside_mode" && !e.confirmed,
                    )
                      ? "primary"
                      : "outline-primary"
                  }
                  onClick={() => void raiseEscalation("bedside_mode")}
                  disabled={!selectedStudyId || persisting}
                >
                  Bedside mode
                </Button>
                <Button
                  size="sm"
                  variant={
                    selectedEscalations.some(
                      (e) =>
                        e.kind === "qeeg_alert_confirmation" && !e.confirmed,
                    )
                      ? "warning"
                      : "outline-warning"
                  }
                  onClick={() => void raiseEscalation("qeeg_alert_confirmation")}
                  disabled={!selectedStudyId || persisting}
                >
                  qEEG confirm required
                </Button>
                <Button
                  size="sm"
                  variant="success"
                  onClick={() => void confirmSelected()}
                  disabled={
                    persisting ||
                    !actorId ||
                    !selectedEscalations.some(
                      (e) => e.requiresConfirmation && !e.confirmed,
                    )
                  }
                >
                  Confirm open alerts
                </Button>
                <Button
                  size="sm"
                  variant={
                    selectedStudy?.firstReviewCompletedAtIso
                      ? "secondary"
                      : "outline-secondary"
                  }
                  onClick={markFirstReview}
                  disabled={!selectedStudyId}
                >
                  {selectedStudy?.firstReviewCompletedAtIso
                    ? "Undo first-60 review"
                    : "Mark first-60 review"}
                </Button>
              </div>
              {selectedEscalations.length === 0 ? (
                <div className="text-muted small">No escalations for selection.</div>
              ) : (
                <div className="d-grid gap-2" data-testid="icu-escalation-list">
                  {selectedEscalations.map((event) => (
                    <div className="border rounded p-2 small" key={event.eventId}>
                      <div className="d-flex justify-content-between gap-2">
                        <strong>{ESCALATION_LABELS[event.kind]}</strong>
                        <Badge
                          bg={event.confirmed ? "success" : "warning"}
                          text={event.confirmed ? undefined : "dark"}
                        >
                          {event.confirmed ? "Confirmed" : "Pending"}
                        </Badge>
                      </div>
                      <div className="text-muted">{event.message}</div>
                      <div className="text-muted">
                        Raised {new Date(event.atIso).toLocaleString()}
                        {event.confirmedBy ? ` by ${event.confirmedBy}` : ""}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <Card>
            <Card.Header>Trends→raw + seizure templates</Card.Header>
            <Card.Body>
              <div className="small mb-3">
                <strong>Trend panels shown:</strong>{" "}
                {trendPanelLabels.join(", ")}. These are qEEG review aids for
                navigating long ICU recordings; confirm
                report-impacting findings on raw EEG.
              </div>
              <div
                className="mb-3"
                data-testid="icu-live-qeeg-trends"
                style={{ minHeight: 160 }}
              >
                {!selectedStudy?.recordingId ? (
                  <div className="small text-muted">
                    Select a study with a recording ID to load live aEEG/CDSA
                    trends.
                  </div>
                ) : liveTrendStatus === "loading" ? (
                  <div className="small text-muted">Loading long-study qEEG trends…</div>
                ) : liveTrendStatus === "generating" ? (
                  <div className="small text-muted">
                    Long-study trends are generating — open the recording to
                    poll progress.
                  </div>
                ) : liveTrendStatus === "unavailable" ||
                  liveTrendStatus === "failed" ? (
                  <div className="small text-muted">
                    Long-study trends unavailable for this recording. Open raw
                    + qEEG to compute from the viewer window.
                  </div>
                ) : (
                  <Suspense
                    fallback={
                      <div className="small text-muted">Loading trend lanes…</div>
                    }
                  >
                    <QEEGTrendLane
                      sampleRate={
                        liveTrendProvenance?.samplingRateHz || 256
                      }
                      currentTimeSeconds={0}
                      recordingDurationSeconds={Math.max(
                        1,
                        ...(liveTrendSeries || []).flatMap((s) =>
                          s.samples.map((p) => p.timeSeconds),
                        ),
                      )}
                      longStudySeries={liveTrendSeries}
                      longStudyStatus={
                        liveTrendStatus === "partial" ||
                        liveTrendStatus === "ready"
                          ? liveTrendStatus
                          : "unavailable"
                      }
                      provenanceOverride={liveTrendProvenance}
                      initialVisibleLanes={
                        enabledTrendTypes.length
                          ? enabledTrendTypes
                          : undefined
                      }
                      spectrogramMatrix={
                        liveTrendSeries?.find(
                          (s) =>
                            s.type === "spectrogram" || s.type === "dsa",
                        )?.matrix || undefined
                      }
                      halfWindowSeconds={
                        surface.rawLinkedLayout.syncWindowSeconds || 30
                      }
                    />
                  </Suspense>
                )}
              </div>
              <div className="small mb-3">
                <strong>Raw/qEEG layout:</strong>{" "}
                {surface.rawLinkedLayout.orientation} split with{" "}
                {(surface.rawLinkedLayout.rawPaneRatio * 100).toFixed(0)}% of the
                width reserved for raw EEG and a{" "}
                {surface.rawLinkedLayout.syncWindowSeconds}s synchronized
                trend-to-raw time window.
              </div>
              <div className="small mb-2">
                Saved templates for this admission: {templates.length}
              </div>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => void addTemplateAndScreen()}
                disabled={!selectedStudyId || !actorId || persisting}
              >
                Add admission template + screen candidate
              </Button>
              {screeningNote && (
                <Alert className="mt-3 mb-0 py-2" variant="info">
                  {screeningNote}
                </Alert>
              )}
              <div className="small text-muted mt-3">
                Open recording:{" "}
                {selectedStudy?.recordingId ? (
                  <Link
                    to={`/eeg/${selectedStudyId}?recordingId=${selectedStudy.recordingId}`}
                  >
                    Review raw + qEEG
                  </Link>
                ) : (
                  "Unavailable until the selected DB worklist study has a recording ID"
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
